import React from 'react';
import { alpha, Button, Menu, Stack } from '@mui/material';
import { theme } from '../../../../../../common/styles/theme';
import { IconMenuItem } from '../../../../../../common/components/IconMenuItem/IconMenuItem';
import { ConfirmPopup } from '../../../../ConfirmPopup/ConfirmPopup';
import { DownloadIcon } from '../../../../../../../icons/DownloadIcon';
import { ListIcon } from '../../../../../../../icons/ListIcon';
import { TrashIcon } from '../../../../../../../icons/TrashIcon';
import { ArrowDropIcon } from '../../../../../../../icons/ArrowDropIcon';
import { IRecordingActionsProps } from './types';
import { AudioIcon } from '../../../../../../../icons/AudioIcon';
import { ListNumberIcon } from '../../../../../../../icons/ListNumberIcon';
import { TextCaptionsIcon } from '../../../../../../../icons/TextCaptionsIcon';
import { TextCodesIcon } from '../../../../../../../icons/TextCodesIcon';
import { useIsMobileLayout } from '../../../../../../common/hooks/useIsMobileLayout';
import { mobileSheetProps, mobileSheetSx } from '../../../../../../common/components/MobileSheet/mobileSheet';

// Popover can't flip on its own: open towards the side with more room
const isMoreRoomAbove = (el: HTMLElement) => {
  const { top, bottom } = el.getBoundingClientRect();
  return window.innerHeight - bottom < top;
};

const popoverOrigins = (up: boolean, horizontal: 'left' | 'center') => ({
  anchorOrigin: { vertical: up ? 'top' : 'bottom', horizontal },
  transformOrigin: { vertical: up ? 'bottom' : 'top', horizontal },
} as const);

// the recording tile's menu actions that the settings popup lacks: downloads and delete
export const RecordingActions: React.FC<IRecordingActionsProps> = ({
  hasCaptions,
  isProcessing,
  isCallUnprocessed,
  onDownload,
  onDownloadCaptions,
  onDownloadCaptionsWithTimecodes,
  onDelete,
}) => {
  const [downloadAnchor, setDownloadAnchor] = React.useState<HTMLElement | null>(null);
  const [openUp, setOpenUp] = React.useState(false);
  const deleteBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const isMobileLayout = useIsMobileLayout();

  // the item shows a loader until the file is ready, then the menu closes
  const download = (action: () => Promise<any>) => action().finally(() => setDownloadAnchor(null));

  const iconSx = { width: theme.spacing(2), height: theme.spacing(2) };

  return (
    <Stack
      direction={'row'}
      justifyContent={'flex-end'}
      alignItems={'center'}
      gap={1}
    >
      <Button
        ref={deleteBtnRef}
        variant={'text'}
        color={'error'}
        startIcon={<TrashIcon sx={iconSx} />}
        sx={{
          flexShrink: 0,
          whiteSpace: 'nowrap',
          height: theme.spacing(5),
          p: theme.spacing(1.5, 2),
          borderRadius: theme.spacing(.75),
          fontSize: theme.typography.subtitle2.fontSize,
          fontWeight: 600,
          textTransform: 'none',

          '&:hover': {
            background: alpha(theme.palette.error.main, .08),
          },
          '&:active': {
            background: alpha(theme.palette.error.main, .16),
          },
        }}
        onClick={(e) => {
          setOpenUp(isMoreRoomAbove(e.currentTarget));
          setConfirmOpen(true);
        }}
      >
        Delete recording
      </Button>
      <ConfirmPopup
        anchor={confirmOpen ? deleteBtnRef.current : null}
        text={'Delete this recording?'}
        confirmTitle={'Delete'}
        closeTitle={'Cancel'}
        checkboxLabel={'Delete for everyone'}
        popoverProps={popoverOrigins(openUp, 'center')}
        onConfirm={({ checked }) => {
          setConfirmOpen(false);
          onDelete(!!checked);
        }}
        onClose={() => setConfirmOpen(false)}
      />

      <Button
        variant={'outlined'}
        disabled={isProcessing || isCallUnprocessed}
        startIcon={<DownloadIcon sx={iconSx} />}
        endIcon={<ArrowDropIcon />}
        onClick={(e) => {
          setOpenUp(isMoreRoomAbove(e.currentTarget));
          setDownloadAnchor(e.currentTarget);
        }}
      >
        Download
      </Button>
      <Menu
        anchorEl={downloadAnchor}
        open={!!downloadAnchor}
        {...(isMobileLayout ? mobileSheetProps : popoverOrigins(openUp, 'left'))}
        sx={isMobileLayout ? mobileSheetSx : undefined}
        onClose={() => setDownloadAnchor(null)}
      >
        <IconMenuItem title={'Audio'} onClick={() => download(onDownload)}>
          <AudioIcon />
        </IconMenuItem>
        {hasCaptions &&
          <IconMenuItem title={'Captions'} onClick={() => download(onDownloadCaptions)}>
            <TextCaptionsIcon />
          </IconMenuItem>
        }
        {hasCaptions &&
          <IconMenuItem title={'Captions with timecodes'} onClick={() => download(onDownloadCaptionsWithTimecodes)}>
            <TextCodesIcon />
          </IconMenuItem>
        }
      </Menu>

    </Stack>
  );
};
